import {
  CssFontFamily,
  CssFontStretch,
  CssFontStyle,
  CssFontWeight,
  JavascriptFontStretch,
  JavascriptFontStyle,
  JavascriptFontWeight,
  StretchConversionArray,
  StyleConversionArray,
  WeightConversionArray,
} from './FormattingConverterConstants';
import { IFormattingConverterObject } from './IFormattingConverterObject';
import { TextFontUtil } from './utils/TextFontUtil';
import { endsWithString, isNullOrEmptyString, startsWithString } from './utils/TypeUtil';

export class TextStyleFormattingConverters {
  /**
   * Firefox does not recognize fonts that have modifiers like 'Arial Narrow' or 'Cooper Black'.
   * Look at the font names and add fallbacks for these fonts (Arial and Cooper respectively), and
   * add the appropriate css to get the desired font styling.
   *
   * Firefox knows of this issue, but seems uninterested in fixing it: https://bugzilla.mozilla.org/show_bug.cgi?id=644385
   *
   * Chrome also has some problems with some fonts but not all. For example it understands
   * "BentonSans Medium" but not "BentonSans Regular"
   *
   * This method cleans up the font-family and related properties for the specified IFormattingConverterObject.
   * @param obj object to clean up font-family and related properties
   */
  static cleanUpFontFamilyStyling(obj: IFormattingConverterObject): void {
    let fontFamily = obj.getFontFamily();
    if (!isNullOrEmptyString(fontFamily)) {
      const newCss: Record<string, never> = {};
      const fontFaces = [];
      fontFaces.push(fontFamily);
      if (startsWithString(fontFamily, "'") || startsWithString(fontFamily, '"')) {
        // strip leading and trailing quotes
        fontFamily = fontFamily.substr(1, fontFamily.length - 2);
      }

      fontFamily = TextStyleFormattingConverters.pullFontPropertyIntoCSS(
        obj,
        fontFaces,
        newCss,
        fontFamily,
        StyleConversionArray,
        CssFontStyle,
        JavascriptFontStyle,
        false
      );

      fontFamily = TextStyleFormattingConverters.pullFontPropertyIntoCSS(
        obj,
        fontFaces,
        newCss,
        fontFamily,
        WeightConversionArray,
        CssFontWeight,
        JavascriptFontWeight,
        true // Only consider the first match to avoid, say, matching both "semibold" and "bold"
      );

      TextStyleFormattingConverters.pullFontPropertyIntoCSS(
        obj,
        fontFaces,
        newCss,
        fontFamily,
        StretchConversionArray,
        CssFontStretch,
        JavascriptFontStretch,
        false
      );

      TextFontUtil.addFallbackFonts(fontFaces);

      if (fontFaces.length > 1) {
        // add the font family fallbacks
        newCss[CssFontFamily] = fontFaces.join(',') as never;
        obj.setCSS(newCss);
      }
    }
  }

  /**
   * Searches for known font modifiers. If one is found, it is removed from the fontFamily, and added to the newCss
   * unless it is overridden with an existing font modifier.
   * @param obj the jQuery object
   * @param fontFallbacks array of font family fallbacks
   * @param newCss css properties that will be applied to the span
   * @param fontFamily The current font family
   * @param conversions a set of conversions to look through (must iterate through from the start to the end in order)
   * @param cssProperty name of the css property to modify
   * @param javascriptCSSProperty name of the css property as a javascript property (usually camelCase)
   * @param stopAfterFirstMatch quit after the first match is found
   * @returns the rest of the font with the modifier removed
   */
  private static pullFontPropertyIntoCSS(
    obj: IFormattingConverterObject,
    fontFallbacks: string[],
    newCss: Record<string, never>,
    fontFamily: string,
    conversions: [string, string][],
    cssProperty: string,
    javascriptCSSProperty: string,
    stopAfterFirstMatch: boolean
  ): string {
    const lowerFontFamily = fontFamily.toLowerCase();
    for (const conversion of conversions) {
      if (endsWithString(lowerFontFamily, conversion[0])) {
        // trim off the modifier from the font family
        fontFamily = fontFamily.substr(0, lowerFontFamily.lastIndexOf(conversion[0])).trim();
        fontFallbacks.push("'" + fontFamily + "'");
        if (!TextStyleFormattingConverters.isCSSPropertySet(obj, javascriptCSSProperty)) {
          newCss[cssProperty] = conversion[1] as never;
        }
        if (stopAfterFirstMatch) {
          // We've found our one match; finish up
          return fontFamily;
        }
      }
    }
    return fontFamily;
  }

  /**
   * Checks if the given property was manually set on the element.
   * drockwood: BUGZID 153705 (http://fogbugz/default.asp?153705)
   * this check is pretty hacky, but I don't know a better way to
   * check if a css property without getting the browser computed value or default
   * @param obj the element wrapped in jQuery
   * @param javascriptPropertyName the name of the css property as a javascript property
   * @returns true if the property is set or false otherwise
   */
  private static isCSSPropertySet(obj: IFormattingConverterObject, javascriptPropertyName: string) {
    return obj.hasStyleProperty(javascriptPropertyName);
  }
}
